import React, { Suspense } from "react";
import "@/index.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";

import PublicLayout from "@/components/Layout/PublicLayout";
import PublicPage from "@/pages/PublicPage";
import NewsDetailPage from "@/pages/NewsDetailPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { LanguageProvider } from "@/lib/i18n";

const AdminLogin = React.lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = React.lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("@/pages/admin/AdminDashboard"));
const PagesManager = React.lazy(() => import("@/pages/admin/PagesManager"));
const NavigationManager = React.lazy(() =>
  import("@/pages/admin/NavigationManager")
);
const NewsManager = React.lazy(() => import("@/pages/admin/NewsManager"));
const MediaManager = React.lazy(() => import("@/pages/admin/MediaManager"));
const TranslationsManager = React.lazy(() =>
  import("@/pages/admin/TranslationsManager")
);
const LeadsManager = React.lazy(() => import("@/pages/admin/LeadsManager"));
const SettingsManager = React.lazy(() =>
  import("@/pages/admin/SettingsManager")
);

const PUBLIC_PAGES = [
  ["/", "home"],
  ["/preventum-diez", "preventum-diez"],
  ["/preventum-diez/fuer-wen", "preventum-diez-fuer-wen"],
  ["/preventum-diez-fakten", "preventum-diez-fakten"],
  ["/medizin-und-untersuchungen", "medizin-und-untersuchungen"],
  ["/kardiologie-diez", "kardiologie-diez"],
  ["/untersuchungen", "untersuchungen"],
  ["/praevention", "praevention"],
  ["/sekundaerpraevention", "sekundaerpraevention"],
  ["/sportmedizin-leistungssport", "sportmedizin-leistungssport"],
  ["/wissen", "wissen"],
  ["/wissen/sport-in-jedem-alter", "wissen-sport-in-jedem-alter"],
  ["/wissen/sport-nach-herzinfarkt", "wissen-sport-nach-herzinfarkt"],
  [
    "/wissen/untersuchung-vor-sportbeginn",
    "wissen-untersuchung-vor-sportbeginn",
  ],
  ["/news", "news"],
  ["/faq", "faq"],
  ["/glossar", "glossar"],
  ["/praxis-und-kontakt", "praxis-und-kontakt"],
  ["/dr-kirchner", "dr-kirchner"],
  ["/ablauf", "ablauf"],
  ["/standort-diez", "standort-diez"],
  ["/kontakt", "kontakt"],
  ["/impressum", "impressum"],
  ["/datenschutz", "datenschutz"],
];

const REDIRECTS = [
  ["/kardiologie", "/kardiologie-diez/"],
  ["/kardiologie/", "/kardiologie-diez/"],
  ["/sportmedizin", "/sportmedizin-leistungssport/"],
  ["/sportmedizin/", "/sportmedizin-leistungssport/"],
  ["/wissenswertes", "/wissen/"],
  ["/wissenswertes/", "/wissen/"],
  ["/standorte", "/standort-diez/"],
  ["/standorte/", "/standort-diez/"],
];

function AdminBoundary() {
  return (
    <AuthProvider>
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center text-ink-muted">
            CMS wird geladen …
          </div>
        }
      >
        <Outlet />
      </Suspense>
    </AuthProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors closeButton />

        <LanguageProvider>
          <Routes>
            <Route element={<PublicLayout />}>
              {PUBLIC_PAGES.map(([route, slug]) => (
                <React.Fragment key={route}>
                  <Route
                    path={route}
                    element={<PublicPage slug={slug} />}
                  />

                  {route !== "/" && (
                    <Route
                      path={`${route}/`}
                      element={<PublicPage slug={slug} />}
                    />
                  )}

                  <Route
                    path={`/en${route === "/" ? "" : route}`}
                    element={<PublicPage slug={slug} />}
                  />

                  <Route
                    path={`/en${route === "/" ? "" : route}/`}
                    element={<PublicPage slug={slug} />}
                  />
                </React.Fragment>
              ))}

              {REDIRECTS.map(([from, to]) => (
                <React.Fragment key={from}>
                  <Route
                    path={from}
                    element={<Navigate to={to} replace />}
                  />
                  <Route
                    path={`/en${from}`}
                    element={<Navigate to={`/en${to}`} replace />}
                  />
                </React.Fragment>
              ))}

              <Route path="/news/:slug" element={<NewsDetailPage />} />
              <Route path="/news/:slug/" element={<NewsDetailPage />} />
              <Route path="/en/news/:slug" element={<NewsDetailPage />} />
              <Route path="/en/news/:slug/" element={<NewsDetailPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Route>

            <Route element={<AdminBoundary />}>
              <Route path="/admin/login" element={<AdminLogin />} />

              <Route path="/admin" element={<AdminLayout />}>
                <Route
                  index
                  element={<Navigate to="/admin/dashboard" replace />}
                />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="pages" element={<PagesManager />} />
                <Route path="navigation" element={<NavigationManager />} />
                <Route path="news" element={<NewsManager />} />
                <Route path="media" element={<MediaManager />} />
                <Route
                  path="translations"
                  element={<TranslationsManager />}
                />
                <Route path="leads" element={<LeadsManager />} />
                <Route path="settings" element={<SettingsManager />} />
              </Route>
            </Route>
          </Routes>
        </LanguageProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
